import { useState, useEffect } from 'react'
import { StatCard } from '@/components/Dashboard/StatCard'
import { api, type AdminStats } from '@/api/client'

function formatStorageSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  const value = bytes / Math.pow(1024, i)
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true)
        const data = await api.getAdminStats()
        setStats(data)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load admin stats')
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-[#718096]">Loading admin stats...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {error}
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-[#1B365D] mb-6">Platform Overview</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label="Total Users"
          value={stats?.totalUsers?.toLocaleString() ?? '0'}
          subtext="registered accounts"
        />
        <StatCard
          label="Total Expenses"
          value={stats?.totalExpenses?.toLocaleString() ?? '0'}
          subtext="across all users"
        />
        <StatCard
          label="Total Documents"
          value={stats?.totalDocuments?.toLocaleString() ?? '0'}
          subtext={`${formatStorageSize(stats?.totalStorageBytes ?? 0)} stored`}
        />
      </div>
    </div>
  )
}
